import { NumberValue } from '../../../utils'
import { InvoiceDiscountType } from '../enums/invoiceDiscountType'
import { InvoiceFormLine } from '../types/invoiceForm/invoiceFormLine/invoiceFormLine'
import type { InvoiceFormRowLine } from '../types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { InvoicePayloadSummary, InvoicePayloadTaxSummary } from '../types/invoicePayload/invoicePayloadSummary'
import { isInvoiceFormDepositLine } from './depositInvoices/isInvoiceFormDepositLine'
import { getSignedAmount } from './getSignedAmount'
import { DiscountValidationIssue, InvoiceDiscountCalculator } from './invoiceDiscountCalculator'
import { isInvoiceFormRowLine } from './isInvoiceFormRowLine'

type InvoiceSummaryTotals = Omit<InvoicePayloadSummary, 'currency'> & {
  validationIssues: DiscountValidationIssue[]
}

interface GetUpdatedTaxSummaryOptions {
  currentSummary: InvoicePayloadTaxSummary[]
  lineTaxTotal: number
  vatRatePercent: number
}

const getUpdatedTaxSummary = ({ currentSummary, lineTaxTotal, vatRatePercent }: GetUpdatedTaxSummaryOptions) => {
  const existingVatRateIndex = currentSummary.findIndex((tax) => tax.percentage === vatRatePercent)

  if (existingVatRateIndex !== -1) {
    return currentSummary.map((tax, index) =>
      index === existingVatRateIndex ? { ...tax, amount: tax.amount + lineTaxTotal } : tax,
    )
  }

  return [
    ...currentSummary,
    {
      amount: lineTaxTotal,
      percentage: vatRatePercent,
    },
  ]
}

// Row lines are always calculated together in one call, whether the discount lives per-line
// (ByItem), on the whole invoice (Global), or not at all (None) - the package distributes and
// reconciles the totals across all three cases, so there's no need for separate code paths here.
const getRowLinesSummaryTotals = (
  rowLines: InvoiceFormRowLine[],
  discountType: InvoiceDiscountType,
  currency: string,
  globalDiscount?: number,
): InvoiceSummaryTotals => {
  const lineInputs = rowLines.map((line) => ({
    discount: line.discount,
    quantity: String(line.quantity),
    unitPrice: String(line.unitPrice),
    vatRate: String(line.vatRatePercent ?? 0),
  }))

  const result = InvoiceDiscountCalculator.calculateInvoice(lineInputs, discountType, currency, globalDiscount)

  const taxSummary = result.lines.reduce<InvoicePayloadTaxSummary[]>((summary, lineResult, index) => {
    const vatRatePercent = rowLines[index].vatRatePercent ?? 0
    return getUpdatedTaxSummary({ currentSummary: summary, lineTaxTotal: lineResult.vatAmount, vatRatePercent })
  }, [])

  return {
    discountTotal: result.totalDiscountAmount,
    grossTotal: result.totalGrossAmount,
    netTotal: result.totalNetAmount,
    subTotal: result.totalLineAmount,
    taxTotal: result.totalVatAmount,
    taxSummary,
    validationIssues: result.validationIssues,
  }
}

const getSummaryTotals = (
  invoiceLines: InvoiceFormLine[],
  discountType: InvoiceDiscountType,
  currency: string,
  globalDiscount?: number,
): InvoiceSummaryTotals => {
  const rowLines = invoiceLines.filter(isInvoiceFormRowLine)
  const depositLines = invoiceLines.filter(isInvoiceFormDepositLine)

  const rowLinesTotals = getRowLinesSummaryTotals(rowLines, discountType, currency, globalDiscount)

  // Deposit invoices are always calculated from the quote's total excluding VAT, which is
  // already the discounted amount - the global discount must not be applied to them again.
  return depositLines.reduce<InvoiceSummaryTotals>((summary, line) => {
    const vatRatePercent = line.vatRatePercent ?? 0
    // Derived from the line's own stored gross/net split, so netTotal + taxTotal always
    // reconciles exactly with grossTotal.
    const lineTaxTotal = new NumberValue(line.totalIncludingVat).subtract(line.totalExcludingVat).getRounded()

    return {
      discountTotal: summary.discountTotal,
      grossTotal: new NumberValue(summary.grossTotal).add(line.totalIncludingVat).getRounded(),
      netTotal: new NumberValue(summary.netTotal).add(line.totalExcludingVat).getRounded(),
      subTotal: new NumberValue(summary.subTotal).add(line.totalExcludingVat).getRounded(),
      taxTotal: new NumberValue(summary.taxTotal).add(lineTaxTotal).getRounded(),
      taxSummary: getUpdatedTaxSummary({ currentSummary: summary.taxSummary, lineTaxTotal, vatRatePercent }),
      validationIssues: summary.validationIssues,
    }
  }, rowLinesTotals)
}

export interface GetInvoiceSummaryTotalsOptions {
  currency: string
  discountType?: InvoiceDiscountType
  globalDiscount?: number
  invoiceLines: InvoiceFormLine[]
  isCreditNote?: boolean
}

export const getInvoiceSummaryTotals = ({
  currency,
  discountType = InvoiceDiscountType.Percentage,
  globalDiscount,
  invoiceLines,
  isCreditNote,
}: GetInvoiceSummaryTotalsOptions): InvoiceSummaryTotals => {
  const totals = getSummaryTotals(invoiceLines, discountType, currency, globalDiscount)

  if (!isCreditNote) {
    return totals
  }

  // InvoiceDiscountCalculator always works with positive amounts (the package rejects negative
  // unit prices), so the credit-note sign is applied once here rather than inside the calculator.
  return {
    ...totals,
    grossTotal: getSignedAmount(totals.grossTotal, isCreditNote),
    netTotal: getSignedAmount(totals.netTotal, isCreditNote),
    subTotal: getSignedAmount(totals.subTotal, isCreditNote),
    taxTotal: getSignedAmount(totals.taxTotal, isCreditNote),
    taxSummary: totals.taxSummary.map((tax) => ({ ...tax, amount: getSignedAmount(tax.amount, isCreditNote) })),
  }
}
